import { DoorOpen, Trophy } from "lucide-react";
import { useEffect, useState } from "react";
import { BoardCanvas } from "../BoardCanvas";
import { type EffectSettings } from "../effects";
import { type GrowthProfile, type GrowthSkill, type RaceView } from "../types";
import { AttackElementMark, TargetElementMark } from "../ui/Elements";
import { Meter } from "../ui/Resources";
import { SkillButton, skillCooldownState, SkillIcon } from "../ui/SkillControls";

export const RACE_DRAFT_LABELS: Record<string, string> = {
  scale_up: "威力增幅",
  cascade_up: "连锁增幅",
  sword_up: "剑势强化",
  staff_up: "法力涌流",
  shield_up: "盾势强化",
  critical_up: "致命强化",
  mana_cap: "法力补给",
  key_burst: "钥能补给",
};

export function RaceScreen({
  view,
  clockNow,
  growth,
  colorBlind,
  effectSettings,
  busy,
  onSlide,
  onDraft,
  onSkill,
  onExit,
}: {
  view: RaceView;
  clockNow: () => number;
  growth: GrowthProfile;
  colorBlind: boolean;
  effectSettings: EffectSettings;
  busy: boolean;
  onSlide: (axis: "row" | "col", index: number, distance: number, hash: string) => Promise<void>;
  onDraft: (choice: string) => Promise<void>;
  onSkill: (skill: string) => Promise<void>;
  onExit: () => void;
}) {
  const race = view.race;
  const [clock, setClock] = useState(clockNow);
  useEffect(() => {
    const timer = window.setInterval(() => setClock(clockNow()), 100);
    return () => window.clearInterval(timer);
  }, [clockNow]);
  const locked = busy || race.draftPending || clock >= race.endsAt;
  const raceElapsedMs = race.startedAt > 0 ? Math.max(0, clock - race.startedAt) : 0;
  const skills = race.skillLoadout
    .map((id) => growth.skills.find((skill) => skill.id === id))
    .filter((skill): skill is GrowthSkill => Boolean(skill));
  return (
    <main className="race-screen">
      <header className="multiplayer-header">
        <div>
          <span className="eyebrow">{race.kind === "daily" ? "每日种子" : "每周种子"}</span>
          <h1>{race.templateName}</h1>
        </div>
        <span className="mutation-chip" title={race.mutation.description}>
          {race.mutation.name}
        </span>
        <button className="icon-button" onClick={onExit} aria-label="退出竞速" title="退出竞速">
          <DoorOpen size={18} />
        </button>
      </header>
      <div className="race-stats">
        <span>
          <strong>{race.depth}</strong>
          <small>层</small>
        </span>
        <span>
          <strong>{race.score}</strong>
          <small>分</small>
        </span>
        <span>
          <strong>{Math.max(0, Math.ceil((race.endsAt - clock) / 1000))}</strong>
          <small>秒</small>
        </span>
      </div>
      <section className="race-opponent" data-effect-target="enemy">
        <div>
          <span className="room-target-name">
            深层敌影
            <TargetElementMark
              resistance={race.monsterResistanceElement}
              weakness={race.monsterWeaknessElement}
            />
          </span>
          <strong>
            {race.monsterHp} / {race.monsterMaxHp}
          </strong>
        </div>
        <Meter value={(race.monsterHp / Math.max(1, race.monsterMaxHp)) * 100} tone="enemy" />
      </section>
      <div className="race-vitals">
        <Meter value={(race.hp / Math.max(1, race.maxHp)) * 100} tone="hp" />
        <span>
          {race.hp} / {race.maxHp}
        </span>
        <span>ATK {race.attack}</span>
        <span>DEF {race.defense}</span>
        <AttackElementMark element={race.weaponElement} />
      </div>
      <div className="race-skill-bar">
        {skills.map((skill) => {
          const cooldown = skillCooldownState(
            race.skillCooldowns,
            race.skillCooldownDurations,
            skill.id,
            raceElapsedMs,
            skill.cooldownMs,
          );
          return (
            <SkillButton
              key={skill.id}
              icon={<SkillIcon skillId={skill.id} />}
              name={skill.name}
              effectTarget={skill.target}
              effectKind={
                skill.target === "self" || skill.target === "all_allies"
                  ? "support"
                  : skill.branch === "warrior"
                    ? "strike"
                    : "magic"
              }
              effectElement={skill.damageElement === "weapon" ? "neutral" : skill.damageElement}
              description={`${skill.description} · 竞速固定 R1`}
              cost={
                race.freeCastReady ? 0 : (race.skillManaCosts?.[skill.id] ?? skill.baseManaCost)
              }
              cooldownRemainingMs={cooldown.remainingMs}
              cooldownDurationMs={cooldown.durationMs}
              disabled={
                locked ||
                cooldown.remainingMs > 0 ||
                (!race.freeCastReady &&
                  race.mana < (race.skillManaCosts?.[skill.id] ?? skill.baseManaCost)) ||
                (skill.id === "free_cast" && !!race.freeCastReady)
              }
              onClick={() => void onSkill(skill.id)}
            />
          );
        })}
      </div>
      {race.draftPending && (
        <section className="race-draft">
          <header>
            <Trophy size={18} />
            <strong>选择一个词条</strong>
          </header>
          <div>
            {race.draftOptions?.map((choice) => (
              <button
                key={choice}
                className="secondary-command compact"
                onClick={() => void onDraft(choice)}
              >
                {RACE_DRAFT_LABELS[choice] ?? "未知词条"}
              </button>
            ))}
          </div>
        </section>
      )}
      <BoardCanvas
        board={race.board}
        boardHash={race.boardHash}
        revision={race.revision}
        cascades={[]}
        transformations={race.lastTransformations ?? []}
        disabled={locked}
        queued={false}
        colorBlind={colorBlind}
        effectSettings={effectSettings}
        fallDelayMs={0}
        lockedRow={-1}
        foggedCells={[]}
        wrapMatches={false}
        woodOreUnion={false}
        onSlide={(axis, index, distance) => onSlide(axis, index, distance, race.boardHash)}
      />
    </main>
  );
}
